import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, CheckCircle2, Loader2,
  Clock, AlertTriangle, Check, ChevronDown, ChevronRight, Sparkles, ArrowRight, Rocket, Zap, TrendingUp, MessageCircle, Send, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import API from '@/utils/api';
import PanelLayout from '@/components/PanelLayout';
import { useTranslation } from 'react-i18next';

const FAQ = [
  { q: 'Jak szybko zobaczę efekty?', a: 'Pierwsze zapytania mogą pojawić się już w 24-72h od uruchomienia. Pełne efekty widać po 2-4 tygodniach regularnych publikacji.' },
  { q: 'Czy muszę sam pisać posty?', a: 'Nie. System tworzy posty za Ciebie. W planie "Działam sam" zatwierdzasz i publikujesz. W planie "Zróbcie to za mnie" robimy wszystko od A do Z.' },
  { q: 'Co zawiera plan za 499 zł/mies.?', a: 'Pełna obsługa marketingowa: przygotowanie oferty, tworzenie reklam, publikacja, monitoring i optymalizacja. Ty tylko odbierasz zapytania.' },
  { q: 'Ile zapytań mogę dostać?', a: 'To zależy od branży i lokalizacji. Średnio użytkownicy zdobywają 5-15 zapytań miesięcznie już w pierwszych tygodniach.' },
  { q: 'Czy mogę anulować w dowolnym momencie?', a: 'Tak, bez zobowiązań. Anulowanie zatrzymuje kolejne płatności — korzystasz do końca opłaconego okresu.' },
  { q: 'Czy płatność jest bezpieczna?', a: 'Tak, korzystamy z Stripe — globalnego lidera płatności online. Twoje dane karty są szyfrowane i nigdy nie trafiają na nasze serwery.' },
  { q: 'Jakie firmy mogą korzystać?', a: 'Hodowcy psów, salony kosmetyczne, mechanicy, firmy remontowe, trenerzy, fotografowie — każda mała firma lokalna, która chce mieć więcej klientów z internetu.' },
];

function FaqItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden cursor-pointer" onClick={() => setOpen(!open)}>
      <div className="flex items-center justify-between px-5 py-4">
        <span className="text-sm font-semibold text-zinc-800">{item.q}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && <div className="px-5 pb-4 text-sm text-zinc-500 leading-relaxed">{item.a}</div>}
    </div>
  );
}

/* Flow step */
function FlowStep({ icon: Icon, label, color, delay, isLast }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="flex flex-col items-center gap-1.5 relative"
    >
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <span className="text-[11px] font-semibold text-zinc-600">{label}</span>
      {!isLast && (
        <ChevronRight className="absolute -right-4 top-3 w-4 h-4 text-zinc-300" />
      )}
    </motion.div>
  );
}

export default function SubskrypcjaPage() {
  const { t } = useTranslation();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const sessionId = searchParams.get('session_id');
  const action = searchParams.get('action');

  const fetchSub = async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`, { headers });
      setSub(res.data);
      return res.data;
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchSub(); }, []);

  useEffect(() => {
    if (!sessionId) return;
    setPolling(true);
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/subscription/checkout-status/${sessionId}`, { headers });
        if (res.data.payment_status === 'paid') {
          toast.success('System uruchomiony!');
          const freshSub = await fetchSub();
          setPolling(false);
          if (action === 'publish' && freshSub?.active) {
            setTimeout(() => navigate('/panel/publikacja?auto=publish'), 500);
          }
          return;
        }
      } catch {}
      setTimeout(poll, 2000);
    };
    poll();
    return () => setPolling(false);
  }, [sessionId]); // eslint-disable-line

  const handleCheckout = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        origin_url: window.location.origin,
        action: 'manage',
        plan_id: planId,
      }, { headers });
      window.location.href = res.data.url;
    } catch {
      toast.error('Błąd tworzenia sesji');
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <PanelLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      </PanelLayout>
    );
  }

  const currentPlan = sub?.plan || 'free';
  const isTrial = sub?.is_trial === true;
  const isActive = sub?.active;
  const trialExpired = isTrial && !isActive;

  return (
    <PanelLayout>
      <div className="max-w-3xl mx-auto py-8 px-4">

        {/* Polling */}
        {polling && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-center" data-testid="payment-processing">
            <Loader2 className="w-5 h-5 animate-spin text-amber-500 mx-auto mb-2" />
            <p className="text-sm font-bold text-amber-700">Uruchamiam system...</p>
          </motion.div>
        )}

        {/* Status banners */}
        {(isTrial && isActive) && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200" data-testid="trial-banner">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-emerald-800">Aktualnie wszystkie funkcje są darmowe</p>
                <p className="text-xs text-emerald-600">Bez ograniczeń, bez karty, bez limitu czasu — płatne plany pojawią się w przyszłości z wyprzedzeniem.</p>
              </div>
            </div>
          </motion.div>
        )}
        {trialExpired && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200" data-testid="trial-expired-banner">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-emerald-800">Wszystko nadal działa — bez opłat</p>
                <p className="text-xs text-emerald-600">Aktualnie wszystkie funkcje są dostępne za darmo. Daj znać jeśli coś przestało działać.</p>
              </div>
            </div>
          </motion.div>
        )}
        {(currentPlan !== 'free' && isActive && !isTrial) && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200" data-testid="active-plan-banner">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <p className="text-sm font-bold text-emerald-800">System aktywny — {currentPlan === 'done_for_you' ? 'pracujemy dla Ciebie' : 'zdobywasz klientów'}</p>
            </div>
          </motion.div>
        )}

        {/* Hero block */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-medium border border-[#F47A3B]/30 text-[#F47A3B] bg-[#F47A3B]/5 mb-4">
            <Rocket className="w-3.5 h-3.5" />
            System zdobywania klientów
          </div>
          <h1 className="text-[28px] sm:text-[32px] font-extrabold text-zinc-900 mb-2 tracking-tight leading-tight" data-testid="sub-title">
            Masz gotowe narzędzie —<br/>
            <span className="text-[#F47A3B]">ale jeszcze nie działa</span>
          </h1>
          <p className="text-[15px] text-zinc-500 max-w-lg mx-auto leading-relaxed">
            Po aktywacji zaczynamy pracować za Ciebie.
          </p>
        </motion.div>

        {/* Value proposition block */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl border border-zinc-200 p-6 mb-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
          data-testid="value-block"
        >
          <div className="space-y-3 mb-5">
            {[
              { icon: Send, text: 'System publikuje za Ciebie', color: 'text-blue-500' },
              { icon: MessageCircle, text: 'Odpowiada klientom', color: 'text-emerald-500' },
              { icon: TrendingUp, text: 'Zamienia zapytania w sprzedaż', color: 'text-[#F47A3B]' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-zinc-50 flex items-center justify-center flex-shrink-0">
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                </div>
                <span className="text-[14px] font-semibold text-zinc-800">{item.text}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[#F47A3B]/5 border border-[#F47A3B]/15">
            <Zap className="w-4 h-4 text-[#F47A3B] flex-shrink-0" />
            <p className="text-[13px] font-bold text-[#F47A3B]">Średnio: 5–15 zapytań / miesiąc</p>
          </div>
        </motion.div>

        {/* How it works — visual flow */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="mb-8"
        >
          <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider text-center mb-4">JAK TO DZIAŁA</p>
          <div className="flex items-start justify-center gap-8 overflow-x-auto pb-2">
            <FlowStep icon={Sparkles} label="Twoja oferta" color="bg-zinc-400" delay={0.1} />
            <FlowStep icon={Send} label="Posty" color="bg-blue-500" delay={0.15} />
            <FlowStep icon={Users} label="Klienci piszą" color="bg-emerald-500" delay={0.2} />
            <FlowStep icon={MessageCircle} label="System odpowiada" color="bg-[#F47A3B]" delay={0.25} />
            <FlowStep icon={TrendingUp} label="Sprzedaż" color="bg-emerald-600" delay={0.3} isLast />
          </div>
        </motion.div>

        {/* Plan cards */}
        <div className="grid md:grid-cols-2 gap-5 mb-8">

          {/* Plan 1: DIY */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl border-2 border-zinc-200 bg-white overflow-hidden hover:border-zinc-300 transition-all duration-300 hover:shadow-lg"
            data-testid="plan-card-diy"
          >
            <div className="p-6">
              <div className="flex items-center gap-2 mb-1">
                <Rocket className="w-5 h-5 text-zinc-600" />
                <h3 className="text-[17px] font-bold text-zinc-900">Chcę działać sam</h3>
              </div>
              <p className="text-xs text-zinc-400 mb-5">Pełna kontrola nad systemem</p>

              <div className="mb-1">
                <span className="text-4xl font-extrabold text-zinc-900">149</span>
                <span className="text-sm text-zinc-400 ml-1.5">zł / mies.</span>
              </div>
              <p className="text-xs text-emerald-500 font-semibold mb-5">To mniej niż 5 zł za reklamę</p>

              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-3">Co możesz zrobić</p>
              <ul className="space-y-3 mb-6">
                {[
                  'Tworzysz reklamy, które przyciągają klientów',
                  'Publikujesz je w odpowiednich grupach',
                  'Zbierasz zapytania w jednym miejscu',
                  'Masz panel klientów i wiadomości',
                  'Widzisz, ile zarabiasz',
                ].map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
                    <span className="text-[13px] text-zinc-600">{f}</span>
                  </li>
                ))}
              </ul>

              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-3">Jak to działa</p>
              <div className="flex items-center justify-center gap-1 mb-5 text-[10px] text-zinc-400 font-medium">
                {['Zdjęcie', 'Reklama', 'Publikacja', 'Zapytania', 'Klient'].map((s, i) => (
                  <span key={s} className="flex items-center gap-1">
                    {s}{i < 4 && <ArrowRight className="w-2.5 h-2.5 text-zinc-300" />}
                  </span>
                ))}
              </div>

              <p className="text-[11px] text-zinc-400 mb-4">Dla osób, które chcą działać samodzielnie</p>

              {/* Test access note */}
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-4 text-center">
                <p className="text-xs font-bold text-amber-700">Teraz możesz przetestować system za darmo</p>
                <p className="text-[10px] text-amber-500 mt-0.5">ograniczony dostęp w ramach testów</p>
              </div>

              {currentPlan === 'diy' && isActive ? (
                <div className="text-center py-3 rounded-xl bg-emerald-100 text-emerald-700 text-sm font-bold" data-testid="current-plan-diy">
                  System aktywny
                </div>
              ) : (
                <>
                  <Button
                    onClick={() => handleCheckout('diy')}
                    disabled={!!checkoutLoading}
                    variant="outline"
                    className="w-full rounded-xl h-auto py-3 text-sm font-bold border-2 border-zinc-300 hover:border-[#F47A3B] hover:text-[#F47A3B] text-zinc-700"
                    data-testid="activate-diy-btn"
                  >
                    {checkoutLoading === 'diy' ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Wypróbuj za darmo <ArrowRight className="w-4 h-4 ml-1" /></>}
                  </Button>
                  <p className="text-[10px] text-zinc-400 text-center mt-2">Bez zobowiązań — testujesz i decydujesz</p>
                </>
              )}
            </div>
          </motion.div>

          {/* Plan 2: Done For You */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="relative rounded-2xl border-2 border-[#F47A3B] bg-zinc-900 overflow-hidden shadow-2xl shadow-[#F47A3B]/15 hover:shadow-[#F47A3B]/25 transition-all duration-300"
            data-testid="plan-card-done_for_you"
          >
            <div className="bg-[#F47A3B] text-center py-2 text-xs font-bold text-white uppercase tracking-wider">
              Najczęściej wybierane
            </div>

            <div className="p-6">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-5 h-5 text-[#F47A3B]" />
                <h3 className="text-[17px] font-bold text-white">Nie mam czasu — zróbcie to za mnie</h3>
              </div>
              <p className="text-xs text-zinc-400 mb-5">My prowadzimy marketing i zdobywamy dla Ciebie klientów</p>

              <div className="mb-1">
                <span className="text-4xl font-extrabold text-[#F47A3B]">499</span>
                <span className="text-sm text-zinc-400 ml-1.5">zł / mies.</span>
              </div>
              <p className="text-xs text-zinc-500 mb-1">w cenie system + pełna obsługa</p>
              <p className="text-xs text-[#F47A3B]/80 font-semibold mb-5">Pierwsze zapytania już w kilka dni</p>

              <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-3">Co robimy za Ciebie</p>
              <ul className="space-y-3 mb-5">
                {[
                  'Przygotowujemy i rozwijamy Twoją ofertę',
                  'Tworzymy reklamy sprzedażowe (grafika + tekst)',
                  'Publikujemy je w odpowiednich grupach',
                  'Monitorujemy i optymalizujemy działania',
                  'Dbamy o to, żeby pojawiały się zapytania',
                ].map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#F47A3B]" />
                    <span className="text-[13px] font-semibold text-zinc-200">{f}</span>
                  </li>
                ))}
              </ul>

              <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-3">Jak to działa</p>
              <div className="flex items-center justify-center gap-1.5 mb-5 text-[10px] text-zinc-400 font-medium">
                {['Oferta', 'Reklama', 'Klient', 'Wiadomość', 'Sprzedaż'].map((s, i) => (
                  <span key={s} className="flex items-center gap-1.5">
                    {s}{i < 4 && <ArrowRight className="w-2.5 h-2.5 text-[#F47A3B]/40" />}
                  </span>
                ))}
              </div>

              {/* Coffee */}
              <div className="bg-[#F47A3B]/[0.08] border border-[#F47A3B]/20 rounded-xl p-4 mb-4 text-center">
                <p className="text-sm font-bold text-white">Ty możesz spokojnie pić kawę ☕</p>
                <p className="text-xs text-[#F47A3B] font-semibold mt-0.5">a my w tym czasie zdobywamy dla Ciebie klientów</p>
              </div>

              {/* Start */}
              <div className="mb-4 text-center">
                <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Start jest prosty</p>
                <p className="text-xs text-zinc-400">Podajesz kilka informacji — my zajmujemy się resztą</p>
              </div>

              <p className="text-[11px] text-zinc-500 mb-4">Najczęściej wybierane przez osoby, które chcą efektów bez uczenia się marketingu</p>

              {currentPlan === 'done_for_you' && isActive ? (
                <div className="text-center py-3 rounded-xl bg-emerald-900/30 border border-emerald-500/30 text-emerald-400 text-sm font-bold" data-testid="current-plan-done_for_you">
                  System aktywny — pracujemy
                </div>
              ) : (
                <Button
                  onClick={() => handleCheckout('done_for_you')}
                  disabled={!!checkoutLoading}
                  className="w-full rounded-xl h-auto py-3.5 text-sm font-bold bg-[#F47A3B] hover:bg-[#E06A2E] text-white shadow-lg shadow-[#F47A3B]/30"
                  data-testid="activate-done_for_you-btn"
                >
                  {checkoutLoading === 'done_for_you' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <span className="flex items-center gap-2">
                      <Rocket className="w-4 h-4" />
                      Chcę klientów — zróbcie to za mnie
                    </span>
                  )}
                </Button>
              )}
            </div>
          </motion.div>

        </div>

        {/* Social proof */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="social-proof">
            <Zap className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <p className="text-[13px] font-bold text-emerald-700">System zaczyna generować zapytania praktycznie od razu po uruchomieniu</p>
          </div>
        </motion.div>

        {/* Trust badges */}
        <div className="flex items-center justify-center gap-6 mb-8 text-xs text-zinc-400 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" />
            Bezpieczna płatność Stripe
          </div>
          <span className="hidden sm:inline">·</span>
          <span>Anuluj kiedy chcesz</span>
          <span className="hidden sm:inline">·</span>
          <span>System rusza natychmiast</span>
        </div>

        {/* FAQ */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-zinc-900 text-center mb-5">Najczęstsze pytania</h2>
          <div className="space-y-3">
            {FAQ.map((item, i) => <FaqItem key={i} item={item} />)}
          </div>
        </div>
      </div>
    </PanelLayout>
  );
}
